/**
 * TerraMatrix Universal Data Platform - SourcesPanel Component
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * Side panel showing connected data sources.
 */

import React from 'react';
import { SourceCard } from './SourceCard';
import { DataSource } from '@/services/api/dataApi';

interface SourcesPanelProps {
  sources: DataSource[];
  isLoading: boolean;
  onClose: () => void;
  onAddSource?: () => void;
  onSelectSource?: (source: DataSource) => void;
  onDiscoverSchema?: (source: DataSource) => void;
  onManageSchema?: (source: DataSource) => void;
  discoveringSourceId?: string | null;
}

export const SourcesPanel: React.FC<SourcesPanelProps> = ({
  sources,
  isLoading,
  onClose,
  onAddSource,
  onSelectSource,
  onDiscoverSchema,
  onManageSchema,
  discoveringSourceId,
}) => {
  return (
    <div className="w-80 border-l border-border bg-card flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
            />
          </svg>
          Data Sources
        </h2>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1"
          aria-label="Close sources panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Sources List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
        ) : sources.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <svg
              className="w-12 h-12 mx-auto mb-4 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
              />
            </svg>
            <p className="mb-2">No data sources connected</p>
            <p className="text-sm">Add a data source to start querying</p>
          </div>
        ) : (
          sources.map((source) => (
            <SourceCard
              key={source.dataSourceId}
              source={source}
              onClick={() => onSelectSource?.(source)}
              onDiscoverSchema={onDiscoverSchema}
              onManageSchema={onManageSchema}
              isDiscovering={discoveringSourceId === source.dataSourceId}
            />
          ))
        )}
      </div>

      {/* Add Source Button */}
      <div className="p-4 border-t border-border">
        <button
          onClick={onAddSource}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg
                     hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Data Source
        </button>
      </div>
    </div>
  );
};

export default SourcesPanel;
